import { getValidatedLimitParam, getValidatedPageParam, useCustomToast } from '@elkaso-app/web-design';
import http from 'apis/config/http-service';
import { useQueryWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import { useIsLoggedIn } from 'hooks/use-is-logged-in';
import { TParam, usePageParams } from 'hooks/use-page-params';
import { UseQueryOptions, UseQueryResult } from 'react-query';

const endpoint = 'ums/v1/suppliers';
export const getSupplierServiceRegionsEndpointIdentifier = endpoint;

type TData = Record<string, any>;
type TError = AxiosError;
type TQueryKey = [
  string,
  {
    page: TParam;
    limit: TParam;
    sort: TParam;
  }
];
type Options = Omit<UseQueryOptions<unknown, TError, TData, TQueryKey>, 'queryKey' | 'queryFn'>;
type ReturnType = UseQueryResult<TData, TError>;

export function useGetSupplierServiceRegionsApi(supplierId: number, options?: Options): ReturnType {
  const toast = useCustomToast();
  const isLoggedIn = useIsLoggedIn();
  const page = getValidatedPageParam();
  const limit = getValidatedLimitParam();
  const { getPageParams } = usePageParams();
  const { sort } = getPageParams();

  const fullEndpoint = `${endpoint}/${supplierId}/regions`;

  const queryFn = async () => {
    try {
      const { data } = await http.get(fullEndpoint, {
        params: {
          page,
          limit,
          sort,
        },
      });

      return data;
    } catch (error: any) {
      if (!error.response.status || !error.response.data.message) throw error;

      toast({
        description: error.response.data.message,
        status: 'error',
      });

      throw error;
    }
  };

  return useQueryWrapper(
    [
      fullEndpoint,
      {
        page,
        limit,
        sort,
      },
    ],
    queryFn,
    {
      enabled: isLoggedIn,
      ...options,
    }
  );
}
